import { FeatureBadge, Icon, Text } from '@driftt/dds'
import { faSparkles } from '@driftt/dds.icons/solid/faSparkles'
import React, { ReactElement } from 'react'

import {
  scWindDown,
  userHasAIPlaybooks,
  userHasBionicChatbots,
  userHasChatExperience,
  userHasChatPlaybooks,
  userHasContentRecommendations,
  userHasContentVariables,
  userHasEmailPlaybooks,
  userHasFastlanePlaybooks,
  userHasGlobalResponses,
  userHasMeetingBookingAppExperience,
  userHasMeetingsPlaybooks,
  userHasReusableBlocksV2,
  userHasSearchAppExperience,
} from 'utils/access'

interface SubNavNode {
  label: string | ReactElement
  path?: string
  hide?: boolean
  exact?: boolean
  external?: boolean
  expanded?: boolean
  nodes?: SubNavNode[]
  'data-ocweb-id'?: string
}

export const getSubNavNodes = (): SubNavNode[] => {
  const hasFastlaneAccess = userHasFastlanePlaybooks()
  const playbooksList = [
    {
      label: 'Chat playbooks',
      path: '/list/chat',
      hide: !userHasChatPlaybooks(),
      exact: true,
    },
    {
      label: 'Fastlane playbooks',
      path: '/list/fastlane',
      hide: !hasFastlaneAccess,
      exact: true,
    },
    {
      label: 'Email playbooks',
      path: '/list/email',
      hide: !userHasEmailPlaybooks(),
      exact: true,
    },
    {
      label: 'Meeting playbooks',
      path: '/list/meetings',
      hide: !userHasMeetingsPlaybooks(),
      exact: true,
      'data-ocweb-id': 'meeting-playbooks-link',
    },
  ]
  const needsToWrap =
    userHasAIPlaybooks() ||
    userHasGlobalResponses() ||
    userHasReusableBlocksV2() ||
    userHasContentRecommendations()

  const hasSiteConciergeAccess =
    (userHasContentRecommendations() ||
      userHasMeetingBookingAppExperience() ||
      userHasSearchAppExperience() ||
      userHasChatExperience()) &&
    !scWindDown()

  const userHasIntelligentSearch =
    scWindDown() && (userHasSearchAppExperience() || userHasChatExperience())

  return [
    {
      label: 'SITE CONCIERGE ',
      hide: !hasSiteConciergeAccess,
      exact: false,
      external: false,
      expanded: true,
      nodes: [
        {
          label: 'Chat',
          path: '/site-concierge/chat',
          hide: !userHasChatExperience(),
          exact: false,
        },
        {
          label: 'Content recommendations',
          path: '/site-concierge/content-recommendations',
          hide: !userHasContentRecommendations(),
          exact: false,
        },
        {
          label: 'Meeting booking',
          path: '/site-concierge/meeting-booking',
          hide: !userHasMeetingBookingAppExperience(),
          exact: false,
        },
        {
          label: 'Intelligent search',
          path: '/site-concierge/search',
          hide: !userHasSearchAppExperience(),
          exact: false,
        },
        {
          label: 'Settings',
          path: '/site-concierge/settings',
          hide: false,
          exact: false,
        },
      ],
    },
    ...(needsToWrap
      ? [
          {
            label: 'PLAYBOOKS',
            hide: false,
            exact: false,
            external: false,
            expanded: true,
            nodes: playbooksList,
          },
        ]
      : playbooksList),
    {
      label: 'Intelligent Search ',
      hide: !userHasIntelligentSearch,
      exact: false,
      external: false,
      expanded: true,
      nodes: [
        {
          label: 'Intelligent search',
          path: '/intelligent-search/search',
          hide: !userHasSearchAppExperience(),
          exact: false,
        },
        {
          label: 'Appearance',
          path: '/intelligent-search/appearance',
          hide: false,
          exact: false,
        },
      ],
    },
    {
      label: 'Library',
      hide:
        !userHasBionicChatbots() &&
        !userHasAIPlaybooks() &&
        !userHasGlobalResponses() &&
        !userHasReusableBlocksV2(),
      exact: false,
      external: false,
      expanded: true,
      nodes: [
        {
          label: (
            <Text>
              <Icon
                icon={faSparkles}
                size="extra-small"
                css={{ verticalAlign: 'baseline', marginRight: '32px' }}
              />
              {`Scenarios`}
              {!window.location.pathname.includes('scenarios') && (
                <FeatureBadge variant="new" css={{ marginLeft: '$1' }} />
              )}
            </Text>
          ),
          path: '/ai-scenarios',
          hide: !userHasBionicChatbots(),
          exact: true,
        },
        {
          label: 'AI topics',
          path: '/playbooks/ai-topics',
          hide: !userHasAIPlaybooks(),
          exact: false,
          external: true,
        },
        {
          label: 'Reusable blocks',
          path: '/blocks/list',
          hide: !userHasReusableBlocksV2(),
          exact: true,
        },
        {
          label: 'Content variables',
          path: '/global/variables',
          hide: !userHasContentVariables(),
          exact: true,
        },
        {
          label: 'Global bot responses',
          path: '/playbook-settings/responses',
          hide: !userHasGlobalResponses(),
          exact: true,
          external: true,
        },
      ],
    },
    {
      label: 'AI testing',
      hide: !userHasBionicChatbots(),
      exact: false,
      external: false,
      expanded: true,
      nodes: [
        {
          label: (
            <Text>
              <Icon
                icon={faSparkles}
                size="extra-small"
                css={{ verticalAlign: 'baseline', marginRight: '32px' }}
              />
              {`AI Responses testing`}
            </Text>
          ),
          path: '/testing',
          hide: !userHasBionicChatbots(),
          exact: true,
        },
      ],
    },
  ]
}
